import { useCallback, useEffect, useState } from "react";
import { Building2, Clock, Coffee, Mail, Scale, Send, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const INFORMES_VACIO = {
  smtp_host: "",
  smtp_port: 587,
  smtp_user: "",
  smtp_password: "",
  sender_email: "",
  sender_name: "Ventura HR",
  use_tls: true,
  smtp_activo: false,
  password_guardada: false,
  destinatarios: [],
  dia_informe_mensual: 1,
  informe_mensual_activo: false,
  informe_anual_activo: false,
  aviso_vigencia_activo: false,
};

export default function ConfiguracionSistema() {
  const [form, setForm] = useState({
    tiempo_cortesia_minutos: 15,
    pausa_cafe_minutos: 15,
    fichaje_canales_job_minutos: 2,
  });
  const [informes, setInformes] = useState(INFORMES_VACIO);
  const [destinosTxt, setDestinosTxt] = useState("");
  const [empresas, setEmpresas] = useState([]);
  const [pruebaEmpresaId, setPruebaEmpresaId] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [guardandoInf, setGuardandoInf] = useState(false);
  const [probando, setProbando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/configuracion/sistema");
      setForm({
        tiempo_cortesia_minutos: data.tiempo_cortesia_minutos ?? 15,
        pausa_cafe_minutos: data.pausa_cafe_minutos ?? 15,
        fichaje_canales_job_minutos: data.fichaje_canales_job_minutos ?? 2,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const cargarInformes = useCallback(async () => {
    try {
      const [{ data }, empRes] = await Promise.all([
        api.get("/configuracion/informes-plataforma"),
        api.get("/empresas"),
      ]);
      setInformes({ ...INFORMES_VACIO, ...data, smtp_password: "" });
      setDestinosTxt((data.destinatarios || []).join(", "));
      const list = empRes.data || [];
      setEmpresas(list);
      setPruebaEmpresaId((prev) => prev || (list[0] ? String(list[0].id) : ""));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    cargar();
    cargarInformes();
  }, [cargar, cargarInformes]);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put("/configuracion/sistema", {
        tiempo_cortesia_minutos: Number(form.tiempo_cortesia_minutos),
        pausa_cafe_minutos: Number(form.pausa_cafe_minutos),
        fichaje_canales_job_minutos: Number(form.fichaje_canales_job_minutos),
      });
      toast.success("Configuración guardada");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const parseDestinatarios = () =>
    destinosTxt
      .split(/[,;\s]+/)
      .map((x) => x.trim().toLowerCase())
      .filter((x) => x.includes("@"));

  const guardarInformes = async () => {
    setGuardandoInf(true);
    try {
      const { data } = await api.put("/configuracion/informes-plataforma", {
        ...informes,
        smtp_port: Number(informes.smtp_port) || 587,
        dia_informe_mensual: Number(informes.dia_informe_mensual) || 1,
        destinatarios: parseDestinatarios(),
        smtp_password: informes.smtp_password || "",
      });
      setInformes({ ...INFORMES_VACIO, ...data, smtp_password: "" });
      setDestinosTxt((data.destinatarios || []).join(", "));
      toast.success("Informes de plataforma guardados");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoInf(false);
    }
  };

  const testSmtp = async () => {
    setProbando(true);
    try {
      await guardarInformes();
      const { data } = await api.post("/configuracion/informes-plataforma/test-smtp");
      toast.success(`Prueba enviada a ${data.destinatario}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProbando(false);
    }
  };

  const enviarPrueba = async (tipo) => {
    setProbando(true);
    try {
      const payload = { tipo };
      if (tipo !== "mensual") {
        if (!pruebaEmpresaId) {
          toast.error("Selecciona una empresa para la prueba");
          return;
        }
        payload.empresa_id = Number(pruebaEmpresaId);
        if (tipo === "vigencia") payload.dias_vigencia = 15;
      }
      const { data } = await api.post("/configuracion/informes-plataforma/enviar-prueba", payload);
      toast.success(`Enviado (${data.enviados || 0} destinatario/s)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProbando(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6" data-testid="config-sistema-page">
      <PageHeader
        icon={Settings2}
        title="Configuración de la plataforma"
        subtitle="Parámetros legales, jornada e informes periódicos del SuperAdmin"
        gradient="from-slate-700 via-indigo-800 to-violet-900"
      />

      <div className="rounded-xl border border-indigo-100 bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50/60 p-4">
          <Scale className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" strokeWidth={1.5} />
          <div className="text-sm text-amber-950/90">
            <p className="font-medium">Criterio del Tribunal Supremo</p>
            <p className="mt-1 text-xs leading-relaxed text-amber-900/80">
              Los marcajes dentro del tiempo de cortesía posteriores al inicio de jornada se consideran efectuados al
              inicio de la misma y computan como trabajo efectivo. Las pausas dentro del límite de pausa reconocida
              también se contabilizan como tiempo trabajado. Estas reglas se aplican a trabajadores con control rígido de
              horario, no a administradores ni encargados.
            </p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-3 rounded-xl border border-indigo-100 bg-indigo-50/30 p-4">
            <div className="flex items-center gap-2 text-indigo-900">
              <Clock className="h-4 w-4" strokeWidth={1.5} />
              <Label className="text-sm font-semibold">Tiempo de cortesía (minutos)</Label>
            </div>
            <Input
              type="number"
              min={0}
              max={120}
              value={form.tiempo_cortesia_minutos}
              onChange={(e) => setForm({ ...form, tiempo_cortesia_minutos: e.target.value })}
              data-testid="cortesia-input"
            />
            <p className="text-xs leading-relaxed text-zinc-500">
              Margen tras la hora de entrada para fichar sin que se registre retraso. Si el trabajador marca dentro de
              este periodo, el tiempo se imputa al inicio de jornada (por defecto: 15 min).
            </p>
          </div>

          <div className="space-y-3 rounded-xl border border-violet-100 bg-violet-50/30 p-4">
            <div className="flex items-center gap-2 text-violet-900">
              <Coffee className="h-4 w-4" strokeWidth={1.5} />
              <Label className="text-sm font-semibold">Pausa reconocida / descanso (minutos)</Label>
            </div>
            <Input
              type="number"
              min={0}
              max={60}
              value={form.pausa_cafe_minutos}
              onChange={(e) => setForm({ ...form, pausa_cafe_minutos: e.target.value })}
              data-testid="pausa-cafe-input"
            />
            <p className="text-xs leading-relaxed text-zinc-500">
              Cupo diario de pausa (café, fumar, personal…) que cuenta como tiempo efectivo. Habitual: 15 min si la
              jornada continuada supera 6 horas (por defecto: 15 min). El exceso se descuenta.
            </p>
          </div>

          <div className="space-y-3 rounded-xl border border-sky-100 bg-sky-50/30 p-4 md:col-span-2">
            <div className="flex items-center gap-2 text-sky-900">
              <Send className="h-4 w-4" strokeWidth={1.5} />
              <Label className="text-sm font-semibold">
                Intervalo del job Validar jornada (WhatsApp / Telegram)
              </Label>
            </div>
            <Input
              type="number"
              min={1}
              max={60}
              className="max-w-xs"
              value={form.fichaje_canales_job_minutos}
              onChange={(e) => setForm({ ...form, fichaje_canales_job_minutos: e.target.value })}
              data-testid="fichaje-job-minutos-input"
            />
            <p className="text-xs leading-relaxed text-zinc-500">
              Cada cuántos minutos el servidor revisa si hay que enviar avisos de fichaje por WhatsApp o Telegram
              (1–60). Por defecto: 2.
            </p>
          </div>
        </div>

        <div className="mt-6">
          <Button
            onClick={guardar}
            disabled={guardando}
            data-testid="guardar-config-btn"
            className="rounded-lg bg-indigo-600 hover:bg-indigo-700"
          >
            {guardando ? "Guardando…" : "Guardar configuración"}
          </Button>
        </div>
      </div>

      {/* Informes plataforma */}
      <div className="rounded-xl border border-violet-100 bg-white p-6 shadow-sm" data-testid="informes-plataforma-panel">
        <div className="mb-4 flex items-start gap-3">
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-violet-700" strokeWidth={1.5} />
          <div>
            <h2 className="text-base font-semibold text-zinc-900">Informes periódicos por correo</h2>
            <p className="mt-1 text-xs leading-relaxed text-zinc-500">
              SMTP propio de plataforma (no usa el correo de las empresas). Mensual: listado de empresas y activos.
              Anual: 10 días antes del aniversario de activación. Vigencia: avisos a 30, 15 y 5 días del fin.
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">SMTP host</Label>
            <Input
              value={informes.smtp_host}
              onChange={(e) => setInformes({ ...informes, smtp_host: e.target.value })}
              placeholder="smtp.office365.com"
              data-testid="inf-smtp-host"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Puerto</Label>
            <Input
              type="number"
              value={informes.smtp_port}
              onChange={(e) => setInformes({ ...informes, smtp_port: e.target.value })}
              data-testid="inf-smtp-port"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Usuario SMTP</Label>
            <Input
              value={informes.smtp_user}
              onChange={(e) => setInformes({ ...informes, smtp_user: e.target.value })}
              data-testid="inf-smtp-user"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              Contraseña {informes.password_guardada ? "(guardada · deja vacío para no cambiar)" : ""}
            </Label>
            <Input
              type="password"
              value={informes.smtp_password}
              onChange={(e) => setInformes({ ...informes, smtp_password: e.target.value })}
              autoComplete="new-password"
              data-testid="inf-smtp-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Remitente (email)</Label>
            <Input
              value={informes.sender_email}
              onChange={(e) => setInformes({ ...informes, sender_email: e.target.value })}
              data-testid="inf-sender-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Nombre remitente</Label>
            <Input
              value={informes.sender_name}
              onChange={(e) => setInformes({ ...informes, sender_name: e.target.value })}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border bg-zinc-50 px-3 py-2 md:col-span-2">
            <Label className="text-sm">TLS / STARTTLS</Label>
            <Switch
              checked={!!informes.use_tls}
              onCheckedChange={(v) => setInformes({ ...informes, use_tls: v })}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border bg-zinc-50 px-3 py-2 md:col-span-2">
            <div>
              <Label className="text-sm">SMTP activo</Label>
              <p className="text-[11px] text-zinc-500">Sin activar, los jobs no envían.</p>
            </div>
            <Switch
              checked={!!informes.smtp_activo}
              onCheckedChange={(v) => setInformes({ ...informes, smtp_activo: v })}
              data-testid="inf-smtp-activo"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs">Destinatarios (separados por coma)</Label>
            <Input
              value={destinosTxt}
              onChange={(e) => setDestinosTxt(e.target.value)}
              placeholder="ops@empresa.com, direccion@empresa.com"
              data-testid="inf-destinatarios"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Día del mes · informe mensual (1–28)</Label>
            <Input
              type="number"
              min={1}
              max={28}
              value={informes.dia_informe_mensual}
              onChange={(e) => setInformes({ ...informes, dia_informe_mensual: e.target.value })}
              data-testid="inf-dia-mensual"
            />
          </div>
        </div>

        <div className="mt-4 space-y-2 rounded-lg border border-zinc-100 bg-zinc-50/80 p-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Informe mensual</Label>
            <Switch
              checked={!!informes.informe_mensual_activo}
              onCheckedChange={(v) => setInformes({ ...informes, informe_mensual_activo: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Informe anual (aniversario −10 días)</Label>
            <Switch
              checked={!!informes.informe_anual_activo}
              onCheckedChange={(v) => setInformes({ ...informes, informe_anual_activo: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Avisos de vigencia (30 / 15 / 5 días)</Label>
            <Switch
              checked={!!informes.aviso_vigencia_activo}
              onCheckedChange={(v) => setInformes({ ...informes, aviso_vigencia_activo: v })}
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={guardarInformes}
            disabled={guardandoInf}
            className="rounded-lg bg-violet-600 hover:bg-violet-700"
            data-testid="inf-guardar-btn"
          >
            {guardandoInf ? "Guardando…" : "Guardar informes"}
          </Button>
          <Button type="button" variant="outline" disabled={probando} onClick={testSmtp}>
            Probar SMTP
          </Button>
          <Button type="button" variant="outline" disabled={probando} onClick={() => enviarPrueba("mensual")} data-testid="inf-prueba-mensual">
            Enviar mensual ahora
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-violet-200 bg-violet-50/40 p-3">
          <div className="min-w-[180px] flex-1 space-y-1">
            <Label className="text-xs flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> Empresa (prueba anual / vigencia)</Label>
            <select
              className="flex h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm"
              value={pruebaEmpresaId}
              onChange={(e) => setPruebaEmpresaId(e.target.value)}
            >
              {empresas.map((e) => (
                <option key={e.id} value={e.id}>{e.nombre}</option>
              ))}
            </select>
          </div>
          <Button type="button" variant="outline" size="sm" disabled={probando || !pruebaEmpresaId} onClick={() => enviarPrueba("anual")}>
            Probar anual
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={probando || !pruebaEmpresaId} onClick={() => enviarPrueba("vigencia")}>
            Probar vigencia (15d)
          </Button>
        </div>
      </div>
    </div>
  );
}
